import { SelectWithLabel } from '@adotel/components'
import { useLazyQuery } from '@apollo/client'
import {
  Button,
  Hidden,
  MenuItem,
  Typography,
  useMediaQuery,
  useTheme
} from '@material-ui/core'
import FilterListIcon from '@material-ui/icons/FilterList'
import { matchSorter } from 'match-sorter'
import { useEffect, useRef, useState } from 'react'

import noResultsFound from '../../assets/images/noResultFound.svg'
import AdotelSkeleton from '../../components/AdotelSkeleton'
import { SEARCH_AVAILABILITY_QUERY } from '../../graphql/searchAvailability'
import { getTranslate, lang } from '../../localization'
import {
  Entities,
  SearchAvailabilityInput,
  SearchAvailabilityResponse
} from '../../types/searchAvailability'
import SearchWidgetMain from '../Booking/SearchWidgets/SearchWidgetMain'
import FilterDrawer from './components/FilterDrawer'
import HotelContainer from './components/HotelContainer'
import HotelFilters from './components/HotelFilters'
import useStyles from './styles/index.styles'

const index = () => {
  const classes = useStyles()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const filterParams = new URLSearchParams(location.search)
  const filterFields = filterParams.get('filter')
  const decodedFilterFields = filterFields && decodeURIComponent(filterFields)
  const newFilterFields: SearchAvailabilityInput =
    decodedFilterFields && JSON.parse(decodedFilterFields)
  const [searchAvailabilityInput, setSearchAvailabilityInput] =
    useState<SearchAvailabilityInput>(newFilterFields)
  const [readMode, setreadMode] = useState(true)

  const min = 0

  const [maxPriceRange, setmaxPriceRange] = useState<number>(0)

  const [priceRange, setPriceRange] = useState<number[]>([min, maxPriceRange])
  const [selectedHotelStar, setselectedHotelStar] = useState<number[]>([])
  const [filteredHotels, setfilteredHotels] = useState<Entities[]>()
  const [searchInputVal, setsearchInputVal] = useState<string>('')
  const [selectedFacilities, setSelectedFacilities] = useState<string[]>([])
  const [selectedPropertyType, setSelectedPropertyType] = useState<string[]>([])
  const [sort, setSort] = useState<string>('PRICE_ASC')
  const [data, setdata] = useState<Entities[]>([])
  const [filterOpen, setFilterOpen] = useState(false)

  const sortOptions = [
    { label: 'Highest Price', value: 'PRICE_DESC' },
    { label: 'Lowest Price', value: 'PRICE_ASC' }
  ]

  const [
    getSearchData,
    { data: searchAvailability, loading: loadingForSearchAvailability }
  ] = useLazyQuery<{
    searchAvailability: SearchAvailabilityResponse
  }>(SEARCH_AVAILABILITY_QUERY, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
    onCompleted: (res) => {
      setdata(res.searchAvailability.entities)
      if (res.searchAvailability.entities.length > 0) {
        const maxPrice = Math.max(
          ...res.searchAvailability.entities.map(
            (entity) => entity.entityBasicDetails.maxPrice
          )
        )
        setmaxPriceRange(maxPrice)
      }
    },
    onError: () => {
      setdata([])
    }
  })

  const isUserPriceChange = useRef(false)

  useEffect(() => {
    isUserPriceChange.current = false
    setPriceRange([min, maxPriceRange])
  }, [min, maxPriceRange])

  useEffect(() => {
    getSearchData({
      variables: {
        input: {
          ...searchAvailabilityInput,
          hotelStar: selectedHotelStar,
          hotelFacilitiesIds: selectedFacilities,
          hotelType: selectedPropertyType[0],
          hotelPrice:
            maxPriceRange !== 0
              ? {
                  min: priceRange[0],
                  max: priceRange[1]
                }
              : undefined,
          sort: sort
        }
      }
    })
  }, [selectedHotelStar, selectedFacilities, selectedPropertyType, sort])

  const hasMountedForReadMode = useRef(false)
  useEffect(() => {
    if (!hasMountedForReadMode.current) {
      hasMountedForReadMode.current = true
      return
    }
    if (readMode) {
      getSearchData({
        variables: {
          input: {
            ...searchAvailabilityInput,
            hotelStar: selectedHotelStar,
            hotelFacilitiesIds: selectedFacilities,
            hotelType: selectedPropertyType[0],
            hotelPrice: undefined,
            sort: sort
          }
        }
      })
    }
  }, [readMode])

  const hasMounted = useRef(false)
  useEffect(() => {
    if (!hasMounted.current) {
      hasMounted.current = true
      return
    }

    if (!isUserPriceChange.current) return // skip when it’s from currency reset

    const handler = setTimeout(() => {
      getSearchData({
        variables: {
          input: {
            ...searchAvailabilityInput,
            hotelStar: selectedHotelStar,
            hotelFacilitiesIds: selectedFacilities,
            hotelType: selectedPropertyType[0],
            hotelPrice:
              maxPriceRange !== 0
                ? {
                    min: priceRange[0],
                    max: priceRange[1]
                  }
                : undefined,
            sort: sort
          }
        }
      })
    }, 1500)

    return () => clearTimeout(handler)
  }, [priceRange[0], priceRange[1]])

  useEffect(() => {
    const fullHotelList = data || []
    const newFilteredHotels = searchInputVal
      ? matchSorter(fullHotelList, searchInputVal, {
          keys: ['entityBasicDetails.propertyName']
        })
      : fullHotelList
    setfilteredHotels(newFilteredHotels)
  }, [searchInputVal, loadingForSearchAvailability, data])

  return (
    <>
      <div className={classes.stickyDiv}>
        <div className={classes.searchContainer}>
          <SearchWidgetMain
            setSearchAvailabilityInput={setSearchAvailabilityInput}
            searchAvailabilityInput={searchAvailabilityInput}
            readMode={readMode}
            setreadMode={setreadMode}
            isInResultPage
          />
        </div>
      </div>
      <div className={classes.root}>
        <div className={classes.resultsContainer}>
          <div className={classes.results}>
            {filteredHotels && filteredHotels.length > 0 ? (
              <>
                <div className={classes.header}>
                  <Typography variant="h4" className={classes.headerText}>
                    {lang === 'fa' ? (
                      `${data.length} نتیجه بر اساس جست‌و‌جوی شما یافت شد`
                    ) : (
                      <>
                        Found <strong>{data.length}</strong> results based on
                        your search
                      </>
                    )}
                  </Typography>
                  {isMobile ? (
                    <div className={classes.filterBtn}>
                      <div className={classes.sortContainer}>
                        <Typography variant="h4" className={classes.headerText}>
                          {getTranslate('Sort by')}:
                        </Typography>
                        <div className={classes.selectContainer}>
                          <SelectWithLabel
                            variant="outlined"
                            fullWidth
                            value={sort}
                            noHelperText
                            InputProps={{
                              classes: { notchedOutline: classes.noborder }
                            }}
                            SelectProps={{
                              classes: { root: classes.select }
                            }}
                            onChange={(e) => {
                              setSort(e.target.value)
                            }}
                            size="small">
                            {sortOptions.map((option, idx) => (
                              <MenuItem value={option.value} key={idx}>
                                {getTranslate(option.label)}
                              </MenuItem>
                            ))}
                          </SelectWithLabel>
                        </div>
                      </div>
                      <Button
                        variant="text"
                        startIcon={<FilterListIcon />}
                        onClick={() => setFilterOpen(true)}>
                        <Typography className={classes.filterTitle}>
                          {getTranslate('Filters')}
                        </Typography>
                      </Button>
                    </div>
                  ) : (
                    <div className={classes.sortContainer}>
                      <Typography variant="h4" className={classes.headerText}>
                        {getTranslate('Sort by')}:
                      </Typography>
                      <div className={classes.selectContainer}>
                        <SelectWithLabel
                          variant="outlined"
                          fullWidth
                          value={sort}
                          noHelperText
                          InputProps={{
                            classes: { notchedOutline: classes.noborder }
                          }}
                          SelectProps={{
                            classes: { root: classes.select }
                          }}
                          onChange={(e) => {
                            setSort(e.target.value)
                          }}
                          size="small">
                          {sortOptions.map((option, idx) => (
                            <MenuItem value={option.value} key={idx}>
                              {getTranslate(option.label)}
                            </MenuItem>
                          ))}
                        </SelectWithLabel>
                      </div>
                    </div>
                  )}
                </div>
                <div className={classes.hotels}>
                  {filteredHotels.map((entity, i) => (
                    <HotelContainer
                      key={i}
                      data={entity.entityBasicDetails}
                      searchAvailability={
                        searchAvailability?.searchAvailability
                      }
                      searchAvailabilityInput={searchAvailabilityInput}
                    />
                  ))}
                </div>
              </>
            ) : (
              !loadingForSearchAvailability &&
              filteredHotels &&
              filteredHotels.length < 1 && (
                <div className={classes.noResultFound}>
                  <img src={noResultsFound} className={classes.noresult} />
                  <Typography variant="h2" className={classes.noResults}>
                    {getTranslate('No result Found')}
                  </Typography>
                </div>
              )
            )}
          </div>
          <Hidden smDown>
            <HotelFilters
              data={data}
              isUserPriceChange={isUserPriceChange}
              priceRange={priceRange}
              maxPrice={maxPriceRange}
              searchAvailabilityInput={searchAvailabilityInput}
              selectedFacilities={selectedFacilities}
              selectedHotelStar={selectedHotelStar}
              selectedPropertyType={selectedPropertyType}
              setPriceRange={setPriceRange}
              setSelectedFacilities={setSelectedFacilities}
              setSelectedPropertyType={setSelectedPropertyType}
              setsearchInputVal={setsearchInputVal}
              setselectedHotelStar={setselectedHotelStar}
            />
          </Hidden>
        </div>
      </div>
      <AdotelSkeleton
        open={loadingForSearchAvailability}
        circlesColor={theme.palette.primary.main}
        numberOfCircles={4}
      />
      <FilterDrawer
        open={filterOpen}
        onClose={() => setFilterOpen(false)}
        onOpen={() => {
          setFilterOpen(true)
        }}
        data={data}
        isUserPriceChange={isUserPriceChange}
        priceRange={priceRange}
        maxPriceRange={maxPriceRange}
        searchAvailabilityInput={searchAvailabilityInput}
        selectedFacilities={selectedFacilities}
        selectedHotelStar={selectedHotelStar}
        selectedPropertyType={selectedPropertyType}
        setPriceRange={setPriceRange}
        setSelectedFacilities={setSelectedFacilities}
        setSelectedPropertyType={setSelectedPropertyType}
        setsearchInputVal={setsearchInputVal}
        setselectedHotelStar={setselectedHotelStar}
      />
    </>
  )
}

export default index
